/**
 * Custom Hook for Draft Form Data Management
 * Automatically saves form data and restores it on component mount
 */

import { useEffect, useRef, useCallback } from 'react'
import { saveDraft, getDraft, deleteDraft, cleanupExpiredDrafts } from '@/utils/draftStorage'

interface UseDraftSaveOptions {
  /**
   * Unique identifier for the form page (e.g., 'login', 'register')
   */
  pageId: string
  /**
   * Debounce delay in milliseconds (default: 1000ms)
   */
  debounceMs?: number
  /**
   * Whether to auto-restore draft on mount (default: true)
   */
  autoRestore?: boolean
  /**
   * Callback function when draft is restored
   */
  onRestore?: (draft: any) => void
}

/**
 * Custom hook for managing form draft saves
 * 
 * @example
 * const { savingState } = useDraftSave({
 *   pageId: 'login',
 *   debounceMs: 1000,
 *   onRestore: (draft) => setFormData(draft)
 * })
 * 
 * // In your form's onChange handler:
 * draftSave(updatedFormData)
 */
export function useDraftSave<T extends Record<string, any>>(
  options: UseDraftSaveOptions
) {
  const {
    pageId,
    debounceMs = 1000,
    autoRestore = true,
    onRestore
  } = options

  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)
  const lastSaveTimeRef = useRef<number>(0)
  const savingStateRef = useRef<'idle' | 'saving' | 'saved'>('idle')

  /**
   * Get current saving state
   */
  const getSavingState = useCallback(() => {
    return savingStateRef.current
  }, [])

  /**
   * Perform the actual draft save
   */
  const performSave = useCallback((formData: T) => {
    try {
      savingStateRef.current = 'saving'
      saveDraft(pageId, formData)
      lastSaveTimeRef.current = Date.now()
      savingStateRef.current = 'saved'

      // Reset to idle after a short delay
      setTimeout(() => {
        savingStateRef.current = 'idle'
      }, 500)
    } catch (error) {
      console.error('Failed to save draft:', error)
      savingStateRef.current = 'idle'
    }
  }, [pageId])

  /**
   * Debounced draft save function to be called from form onChange handlers
   */
  const draftSave = useCallback((formData: T) => {
    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    // Set new timer
    debounceTimerRef.current = setTimeout(() => {
      performSave(formData)
      debounceTimerRef.current = null
    }, debounceMs)
  }, [debounceMs, performSave])

  /**
   * Immediately save draft without debouncing
   */
  const saveDraftNow = useCallback((formData: T) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
      debounceTimerRef.current = null
    }
    performSave(formData)
  }, [performSave])

  /**
   * Clear the current page's draft
   */
  const clearDraft = useCallback(() => {
    deleteDraft(pageId)
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
      debounceTimerRef.current = null
    }
  }, [pageId])

  /**
   * Restore draft on mount
   */
  useEffect(() => {
    if (autoRestore) {
      cleanupExpiredDrafts()
      const savedDraft = getDraft<T>(pageId)
      
      if (savedDraft && onRestore) {
        onRestore(savedDraft)
      }
    }

    return () => {
      // Cleanup: clear debounce timer on unmount
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [pageId, autoRestore, onRestore])

  return {
    /**
     * Call this function whenever form data changes
     */
    draftSave,
    /**
     * Call this function to immediately save without debouncing
     */
    saveDraftNow,
    /**
     * Call this function to clear the draft (typically after successful submission)
     */
    clearDraft,
    /**
     * Get current saving state ('idle' | 'saving' | 'saved')
     */
    savingState: getSavingState()
  }
}

export default useDraftSave